import React, { useState, useEffect, ReactNode } from 'react';
import {
  useGetEsopElectionWindowConfiguration,
  useGetSettlementType,
  useWealthlaneAwardApi,
  useWealthlaneAwardSettlementApi,
} from '../../../../../hooks';
import { ElectionCardBlock, ElectionWindowWrapper } from './electionWindow.styled';
import Heading from '../../../../../components/typography';
import ClickableTooltip from '../../../../../components/tooltip/clickTooltip';
import { MaterialIconFilled, checkNaNValue } from '../../../../../utils/global';
import { Form, Formik } from 'formik';
import Button from '../../../../../components/button';
import ElectionCustomDropdown from '../../../../../components/custom-dropdown/electionDropdown';
import { EsopElectionWindowConfigurationService } from '@wealthlane/api-services/wealthlane-award-settlement-service';
import Tag from '../../../../../components/lozenge/tags';
import { DropdownType } from '../../../../../types/common';
import { useMutation, useQueryClient } from 'react-query';
import { SOP_ELECTION_VALIDATION } from '../../../../../utils/validations/validations';
import { toast } from 'react-toastify';
import ToastComponent from '../../../../../components/toast';
import _, { isEmpty } from 'lodash';
import Spinners from '../../../../../components/spinner';

interface RsuElectionType {
  refresher: boolean;
}

const SopElectionConfig = ({ refresher }: RsuElectionType) => {
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneAwardSettlementApi();
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const { data: sopConfig, isLoading: loader } =
    useGetEsopElectionWindowConfiguration(handleRequest);
  const [defaultSopValue, setDefaultSopValue] = useState<string>('');
  const [viewSelected, setViewSelected] = useState<boolean>(false);
  const [apiData, setApiData] = useState<string[]>([]);
  const { data: settlementTypeOption, isLoading: loadingSettlementTypeOption } =
    useGetSettlementType(awardHandleRequest);

  const [initialOptions, setInitialOptions] = useState<any>(settlementTypeOption);
  const [initialData, setInitialData] = useState<any>(sopConfig);

  useEffect(() => {
    if (sopConfig !== null) {
      setInitialData(sopConfig);
    }
  }, [sopConfig]);

  useEffect(() => {
    if (settlementTypeOption !== null && settlementTypeOption !== undefined) {
      setInitialOptions(settlementTypeOption);
    }
  }, [settlementTypeOption]);

  useEffect(() => {
    setApiData(
      sopConfig != null
        ? sopConfig?.settlementTypeIds?.map((item: any) => item?.settlementTypeId)
        : []
    );
  }, [sopConfig]);

  useEffect(() => {
    apiData !== undefined && apiData?.length > 0 && setViewSelected(true);
    apiData !== undefined && !apiData?.includes(defaultSopValue)
      ? setCheckedList([...apiData])
      : setCheckedList(apiData);
  }, [apiData]);

  const handleCheck = (value: string, setField: any, data?: string): void => {
    setField('settlementValidity', isEmpty(checkedList) ? true : viewSelected);
    setViewSelected(true);
    const keys =
      checkedList && checkedList?.length > 0
        ? checkedList?.includes(value)
          ? checkedList.filter((item) => item !== value)
          : [...checkedList, value]
        : [value];
    data && checkedList && !keys.includes(data) && setField('defaultSettlementTypeId', '');

    setCheckedList(keys);
  };

  const transformObjectToNull = (values: any): any => {
    const obj: any = {};
    Object.entries(values)?.forEach(([key, value]) => {
      // We have to convert the values of object which contains boolean value only
      obj[key] = typeof value === 'number' && value === 0 ? null : value;
    });
    obj.settlementValidity = 'invalid';
    return obj;
  };

  const usePostSopElectionConfig = (): any => {
    return useMutation(
      async (payload: any) => {
        return await handleRequest(
          EsopElectionWindowConfigurationService.postApiAwardSettlementApiAppESOpElectionWindowConfigurationSaveESopElectionWindowConfiguration(
            payload
          )
        );
      },
      {
        onSuccess: (response: any) => {
          if (response != null) {
            toast.success(<ToastComponent label='Success' message={'Configuration saved'} />);
            void queryClient.invalidateQueries('getEsopElectionWindowConfigurationList');
          }
        },
        onError: (err) => {
          console.log('error', err);
        },
      }
    );
  };

  const { mutate: mutateSop, isLoading } = usePostSopElectionConfig();

  const handleSubmitRsu = (values: any): void => {
    delete values.settlementValidity;

    const payload = {
      ...values,
      // sellAll: checkedList?.includes('sellAll'),
      // sellToCover: checkedList?.includes('sellToCover'),
      // holdToCover: checkedList?.includes('holdToCover'),
      defaultSettlementConfigurationDto: null,
      setFromDefaultSettlementConfig: false,
      planTypeCategory: 'Options',
      planTypeCategoryId: sopConfig?.planTypeCategoryId,
      electionWindowConfigurationId: sopConfig?.electionWindowConfigurationId ?? '',
      settlementTypeIds: checkedList
        .filter((id, index, arr) => arr.indexOf(id) === index)
        .map((id) => ({ settlementTypeId: id })),
      startFromVestingDateInDays: 0,
      endFromVestingDateInDays: 0,
    };

    if (_.isEqual(payload, sopConfig)) {
      toast.success(<ToastComponent label='Info' message={'No changes made'} />);
    } else {
      mutateSop(payload);
    }
  };

  return (
    <ElectionWindowWrapper id="sopElectionConfigWrapper">
      <div className='flex gap-x-5 mb-2' id="sopElectionConfigHeader">
        <Heading
          variant='h4'
          title={'Set Up Tax Settlement Method - Options'}
          className='election-head'
          id="sopElectionConfigTitle"
        />
      </div>

      <ElectionCardBlock id="sopElectionConfigCardBlock">
        {loader && <Spinners />}
        {!loader && (
          <Formik
            initialValues={initialData != null ? transformObjectToNull(initialData) : {}}
            validationSchema={SOP_ELECTION_VALIDATION}
            onSubmit={(values) => {
              handleSubmitRsu(values);
            }}>
            {({ errors, setFieldValue }) => {
              useEffect(() => {
                setFieldValue('settlementValidity', isEmpty(checkedList) ? true : viewSelected);
              }, [checkedList]);

              return (
                <Form id='sopElectionConfigForm'>
                  <div className='grid lg:grid-cols-12 md:grid-cols-1' id="sopElectionConfigFormContainer">
                    <div className='lg:col-span-4 col-span-1' id="settlementMethodsContainer">
                      <ElectionCustomDropdown
                        name='settlementMethod'
                        title='Tax Settlement Methods Available'
                        display={viewSelected}
                        handleUser={(value: string) => {
                          handleCheck(value, setFieldValue);
                        }}
                        data={settlementTypeOption}
                        checkedList={checkedList}
                        errorMessage={errors?.settlementMethod as string}
                        toggleItems={{
                          displaySelected: true,
                        }}
                      />
                    </div>
                  </div>
                  <div id="saveSopElectionConfigButtonContainer">
                    <Button
                      btnClassName='mt-[16px]'
                      title={`Save`}
                      type='submit'
                      variant={'primary'}
                      loading={isLoading}
                      id="saveSopElectionConfigButton"
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </ElectionCardBlock>
    </ElectionWindowWrapper>
  );
};

export default SopElectionConfig;
